import axios from 'axios';
import { Farm, Sensor, SensorData, Crop, Member, User, CreateFarmRequest, CreateSensorRequest, CreateCropRequest, CreateSensorDataRequest, CreateMemberRequest, LoginRequest, TokenResponse } from '../types';
import { log } from '../utils/logger';
import { API_CONFIG } from '../config';

const api = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  withCredentials: true, // 쿠키 포함하여 전송
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for logging and JWT token
api.interceptors.request.use(
  async (config) => {
    const startTime = Date.now();
    config.metadata = { startTime };

    // JWT 토큰 자동 추가 (TokenManager 사용)
    try {
      const { TokenManager } = await import('../utils/tokenManager');

      // TokenManager 초기화 확인
      TokenManager.initialize();

      // 토큰 상태 디버깅
      console.log('🔍 Axios Interceptor - TokenManager check:', {
        url: config.url,
        isLoggedIn: TokenManager.isLoggedIn(),
        hasAccessToken: !!TokenManager.getAccessToken(),
        hasRefreshToken: !!TokenManager.getRefreshToken(),
        tokenInfo: TokenManager.getTokenInfo()
      });

      const token = await TokenManager.ensureValidToken();

      console.log('🔍 Axios Interceptor - Token result:', {
        url: config.url,
        token: token ? `${token.substring(0, 20)}...` : 'null',
        willAddHeader: !!token
      });

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
        console.log('✅ Axios Interceptor - Authorization header added for:', config.url);
      } else {
        console.log('❌ Axios Interceptor - No token available for:', config.url);
      }
    } catch (error) {
      console.error('❌ Axios Interceptor - Token error for:', config.url, error);
      log.warn('Failed to get auth token', error);
    }

    log.info(`API Request: ${config.method?.toUpperCase()} ${config.url}`, {
      method: config.method?.toUpperCase(),
      url: config.url,
      baseURL: config.baseURL,
      data: config.data,
      hasAuth: !!config.headers.Authorization
    });

    return config;
  },
  (error) => {
    log.error('API Request Error', error);
    return Promise.reject(error);
  }
);

// Response interceptor for logging
api.interceptors.response.use(
  (response) => {
    const endTime = Date.now();
    const duration = endTime - (response.config.metadata?.startTime || endTime);
    
    log.apiCall(
      response.config.method?.toUpperCase() || 'GET',
      response.config.url || '',
      response.status,
      duration
    );
    
    return response;
  },
  (error) => {
    const endTime = Date.now();
    const duration = endTime - (error.config?.metadata?.startTime || endTime);
    
    if (error.response) {
      // Server responded with error status
      log.apiCall(
        error.config?.method?.toUpperCase() || 'GET',
        error.config?.url || '',
        error.response.status,
        duration
      );
      
      log.error(`API Error ${error.response.status}`, {
        method: error.config?.method?.toUpperCase(),
        url: error.config?.url,
        status: error.response.status,
        data: error.response.data,
        duration
      });
    } else if (error.request) {
      // Network error
      log.error('API Network Error', {
        method: error.config?.method?.toUpperCase(),
        url: error.config?.url,
        message: 'No response received',
        duration
      });
    } else {
      // Request setup error
      log.error('API Request Setup Error', {
        message: error.message,
        duration
      });
    }
    
    return Promise.reject(error);
  }
);

// Farm API
export const farmApi = {
  getAll: () => api.get<Farm[]>('/farms/'),
  getById: (id: number) => api.get<Farm>(`/farms/${id}`),
  create: (data: CreateFarmRequest) => api.post<Farm>('/farms/', data),
  update: (id: number, data: Partial<CreateFarmRequest>) => api.put<Farm>(`/farms/${id}`, data),
  delete: (id: number) => api.delete(`/farms/${id}`),
};

// Sensor API
export const sensorApi = {
  getAll: () => api.get<Sensor[]>('/sensors/'),
  getById: (id: number) => api.get<Sensor>(`/sensors/${id}`),
  create: (data: CreateSensorRequest) => api.post<Sensor>('/sensors/', data),
  update: (id: number, data: Partial<CreateSensorRequest>) => api.put<Sensor>(`/sensors/${id}`, data),
  delete: (id: number) => api.delete(`/sensors/${id}`),
  getData: (sensorId: number) => api.get<SensorData[]>(`/sensors/${sensorId}/data`),
  createData: (sensorId: number, data: CreateSensorDataRequest) => api.post<SensorData>(`/sensors/${sensorId}/data`, data),
};

// Crop API
export const cropApi = {
  getAll: () => api.get<Crop[]>('/crops/'),
  getById: (id: number) => api.get<Crop>(`/crops/${id}`),
  create: (data: CreateCropRequest) => api.post<Crop>('/crops/', data),
  update: (id: number, data: Partial<CreateCropRequest>) => api.put<Crop>(`/crops/${id}`, data),
  delete: (id: number) => api.delete(`/crops/${id}`),
  getByFarm: (farmId: number) => api.get<Crop[]>(`/crops/farm/${farmId}`),
};

// Member API
export const memberApi = {
  getAll: () => api.get<Member[]>('/members/'),
  getById: (id: number) => api.get<Member>(`/members/${id}`),
  create: (data: CreateMemberRequest) => api.post<Member>('/members/', data),
  update: (id: number, data: Partial<CreateMemberRequest>) => api.put<Member>(`/members/${id}`, data),
  delete: (id: number) => api.delete(`/members/${id}`),
};

// Auth API
export const authApi = {
  login: (data: LoginRequest) => api.post<TokenResponse>('/auth/login', data),
  refresh: () => api.post<TokenResponse>('/auth/refresh'),
  me: () => api.get<User>('/auth/me'),
  logout: () => api.post('/auth/logout'),
  validate: () => api.post('/auth/validate'),
};

// Notifications API
export const notificationApi = {
  getAll: () => api.get('/notifications'),
  getStats: () => api.get('/notifications/stats/summary'),
  getById: (id: number) => api.get(`/notifications/${id}`),
  markAsRead: (ids: number[]) => api.put('/notifications/mark-read', { notification_ids: ids }),
  delete: (id: number) => api.delete(`/notifications/${id}`),
};

export default api;
